import { useContext, useRef } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic numbered pagination for **server-side** (ES `from`/`size`) result
 * sets. Reactive-json's native Switch pagination is client-side (it slices a
 * fully-loaded array) and so doesn't fit `from`/`size` paging — this primitive
 * instead reads the page metadata the backend already returns
 * (`currentPage`, `totalPages`, `size`) and renders a windowed number strip
 * (`‹ 1 … 4 5 6 … 12 ›`) with ellipses, borrowing the windowing approach of
 * reactive-json's `usePagination`.
 *
 * Clicking a page writes `from = (page - 1) * size` to the store; the refetch
 * is the YAML `actions:` (`submitData`), fired on the bubbled click — same
 * pattern as the facet primitives.
 *
 * Configurable : `maxButtons` (windowing width), `showPrevNext` / `showEllipsis`
 * toggles, and full class overrides (`containerClass`, `buttonClass`,
 * `activeClass`, `navClass`, `ellipsisClass`) so the same primitive can wear a
 * different pagination style per block without code changes.
 *
 * YAML usage:
 *   - type: Pagination
 *     pagination: ~~.esResult.pagination     # {currentPage, totalPages, size}
 *     dataLocation: ~~.pagination.from        # where to write the new offset
 *     maxButtons: 7
 *     showPrevNext: true
 *     showEllipsis: true
 *     offset: 1                # optional — reserve the first N docs of the set
 *                              # for a separate slot (e.g. a fixed "featured"
 *                              # hero rendered from `from: 0`), so the paginated
 *                              # list starts at doc N. Page math is then derived
 *                              # from the envelope's `total`/`size` and clicking
 *                              # page P writes `(P-1)*size + offset`. Default 0 =
 *                              # unchanged (reads server `currentPage`/`totalPages`).
 *     scrollToTop: true        # on page change, smooth-scroll back to the top
 *                              # (pass a CSS selector instead of true to scroll
 *                              #  a specific element into view)
 *     containerClass: '...'   # buttonClass / activeClass / navClass / ellipsisClass
 *     actions:
 *       - { what: submitData, on: click, requestKey: esResult-refresh, url: ~~.esQueryUrl,
 *           httpMethod: POST, data: { filter: ~~.selectedFilters, search: ~~.search, pagination: ~~.pagination },
 *           updateOnlyData: true, updateDataAtLocation: ~~.esResult, refreshAppOnResponse: true }
 */

// Windowed page list with ellipsis tokens ('…'). Mirrors usePagination's
// "keep first/last, window around current" behaviour.
const buildPages = (current, total, max) => {
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1)
  const half = Math.floor(max / 2)
  let start = Math.max(1, current - half)
  const end = Math.min(total, start + max - 1)
  start = Math.max(1, end - max + 1)
  const pages = []
  if (start > 1) {
    pages.push(1)
    if (start > 2) pages.push('…')
  }
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < total) {
    if (end < total - 1) pages.push('…')
    pages.push(total)
  }
  return pages
}

export const Pagination = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const pagination = resolve(props.pagination) || {}
  const size = Number(pagination.size) || 0
  const offset = Number(resolve(props.offset)) || 0

  // With an `offset` (first N docs reserved for a hero/featured slot), the
  // server's `currentPage`/`totalPages` — computed for a 0-based window — no
  // longer line up, so derive the strip from `total`/`size` minus the offset.
  // Without an offset, keep the server's values verbatim (existing blocks).
  let total, current
  if (offset > 0) {
    const totalDocs = Number(pagination.total) || 0
    const from = Number(pagination.from) || 0
    total = size > 0 ? Math.ceil(Math.max(0, totalDocs - offset) / size) : 0
    current = size > 0 ? Math.floor(Math.max(0, from - offset) / size) + 1 : 0
  }
  else {
    total = Number(pagination.totalPages) || 0
    current = Number(pagination.currentPage) || 0
  }

  const maxButtons = Number(resolve(props.maxButtons)) || 7
  const showPrevNext = resolve(props.showPrevNext) !== false
  const showEllipsis = resolve(props.showEllipsis) !== false

  // Class overrides — pass the full class string to fully restyle.
  const idleBtn = 'border-gray-200 text-gray-600 bg-white hover:border-indigo-300 hover:text-indigo-600'
  const baseBtn = 'w-9 h-9 flex items-center justify-center text-sm font-semibold rounded-xl border transition-all disabled:opacity-40 disabled:cursor-not-allowed'
  const containerClass = resolve(props.containerClass) ?? 'flex justify-center items-center gap-1.5'
  const buttonClass = resolve(props.buttonClass) ?? `${baseBtn} ${idleBtn}`
  const activeClass = resolve(props.activeClass) ?? `${baseBtn} bg-indigo-600 border-indigo-600 text-white shadow-md`
  const navClass = resolve(props.navClass) ?? `${baseBtn} ${idleBtn}`
  const ellipsisClass = resolve(props.ellipsisClass) ?? 'w-9 h-9 flex items-center justify-center text-gray-400'

  const { formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: 0,
    globalDataContext,
    templateContext,
  })

  if (total <= 1 || size <= 0) return null

  const goTo = (page) => {
    const clamped = Math.min(Math.max(1, page), total)
    globalDataContext.updateData((clamped - 1) * size + offset, formDataPath)
    const scrollTo = resolve(props.scrollToTop)
    if (scrollTo && typeof window !== 'undefined') {
      // A string is treated as a CSS selector to scroll into view ; otherwise
      // scroll the window back to the top.
      if (typeof scrollTo === 'string') {
        document.querySelector(scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  let pages = buildPages(current, total, maxButtons)
  if (!showEllipsis) pages = pages.filter((p) => p !== '…')

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className={containerClass}>
        {showPrevNext && (
          <button type="button" disabled={current <= 1} onClick={() => goTo(current - 1)} className={navClass} aria-label="Précédent">
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className={ellipsisClass}>…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => goTo(p)}
              aria-current={p === current ? 'page' : undefined}
              className={p === current ? activeClass : buttonClass}
            >
              {p}
            </button>
          )
        )}

        {showPrevNext && (
          <button type="button" disabled={current >= total} onClick={() => goTo(current + 1)} className={navClass} aria-label="Suivant">
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </ActionDependant>
  )
}
